import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Center,
    Heading,
    Img,
    Text
} from "@chakra-ui/react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef } from "react";
import { FaFileDownload } from "react-icons/fa";
import { Logo } from "../../../Utils"; // adjust path if needed

const AbnPdf = ({ customIcdTests = [] }) => {
    const abnRef = useRef(null);
    const testsRef = useRef(null);

    // Helper to shrink width and start at top of page
    const addImageTop = (pdf, imgData) => {
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);

        let imgWidth = pageWidth;
        let imgHeight = (imgProps.height * pageWidth) / imgProps.width;

        if (imgHeight > pageHeight) {
            imgHeight = pageHeight;
            imgWidth = (imgProps.width * pageHeight) / imgProps.height;
        }

        const x = (pageWidth - imgWidth) / 2; // center horizontally
        const y = 0; // always start at top
        pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight, "", "FAST");
    };

    const downloadAbnPdf = async () => {
        const abnInput = abnRef.current;
        const testsInput = testsRef.current;
        if (!abnInput || !testsInput) return;

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "letter",
        });

        // Page 1 (Text only)
        const abnCanvas = await html2canvas(abnInput, { scale: 2 });
        const abnImgData = abnCanvas.toDataURL("image/png", 0.6);
        addImageTop(pdf, abnImgData);

        // Page 2 (Tests + Signature)
        pdf.addPage();
        const testsCanvas = await html2canvas(testsInput, { scale: 2 });
        const testsImgData = testsCanvas.toDataURL("image/png", 0.6);
        addImageTop(pdf, testsImgData);

        pdf.save("ABN.pdf");
    };

    return (
        <>
            {/* Hidden ABN Text (Page 1) */}
            <Card ref={abnRef} w="816px" style={{ position: "absolute", top: "-9999px" }}>
                <CardHeader>
                    <Center mb={7}>
                        <Img src={Logo} w={20} />
                    </Center>
                    <Center>
                        <Heading size="sm">ABN (Advance Beneficiary Notice)</Heading>
                    </Center>
                </CardHeader>
                <CardBody p={10}>
                    <Text whiteSpace="pre-wrap" fontSize="sm" mb={4}>
                        <b>*Note: </b>If Medicare does not pay for laboratory tests below, you may
                        have to pay out of pocket. Medicare does not pay for all tests performed,
                        even some care that you or your healthcare provider have good reason to
                        think you need. Diagnostic Laboratory Science expects Medicare may not pay
                        for the following laboratory tests:
                        {"\n"}Alpha –fetoprotein, serum, Amylase, CA 15-3/27.29, CA 125, CA 19-9,
                        CBC, CEA, Cholesterol, Collagen cross link, Cytogenetic studies, Digoxin,
                        Fecal occult blood, Ferritin, Fructosamine, Gamma glutamyl transferase
                        (GGT), Glucose serum or plasma, Glycohemoglobin A1C, FSH, LH, Gonadotropin,
                        HCG, serum quant., HDL cholesterol, Hematocrit, Hemoglobin, Hepatic
                        function panel (AMA), Hepatitis panel (AMA), Hepatitis C RNA, HIV 1
                        Ab/Reflex Western Blot, HIV 1 RNA, Iron, Iron binding capacity, LDL
                        cholesterol direct, Lipid panel, Lipoprotein electrophoresis, Magnesium,
                        Partial thromboplastin time (PTT), Platelet count, Prothrombin time (PT)
                        with INR, PSA total, PSA free, RPR/VDRL, Sedimentation rate, T3, free, T3
                        total, T4 total, Transferrin, Triglycerides, Troponin, TSH, Urine culture
                        w/ reflex Sensitivity.
                        {"\n\n"}<b>WHAT YOU NEED TO DO NOW:</b>
                        {"\n"}<b>•</b> Read this notice, so you can make an informed decision about
                        your care.
                        {"\n"}<b>•</b> Ask us any questions that you may have after reading this
                        notice.
                        {"\n"}<b>•</b> Choose an option below about whether to receive the
                        laboratory tests listed above.
                        {"\n\n"}*Note: If you choose option 1 or 2, we may help you to use any other
                        insurance that you might have, but Medicare cannot require us to do so.
                        {"\n\n"}<b>Options: Check only ONE box. We cannot choose a box for you.</b>
                        {"\n\n"}<b>□ Option 1.</b> I want the laboratory tests listed above. You may
                        ask to be paid now, but I also want Medicare billed for an official
                        decision on payment, which is sent to me on a Medicare Summary Notice
                        (MSN). I understand that if Medicare does not pay, I am responsible for
                        payment, but I can appeal to Medicare by following the directions on the
                        MSN. If Medicare does pay, you will refund any payments I made to you, less
                        co-pays or deductibles.
                        {"\n\n"}<b>□ Option 2.</b> I want the laboratory tests listed above, but do
                        not bill Medicare. You may ask to be paid now as I am responsible for the
                        payment. I cannot appeal if Medicare is not billed.
                        {"\n\n"}<b>□ Option 3.</b> I do not want the tests listed above. I
                        understand with this I am not responsible for payment, and I cannot appeal
                        to see if Medicare would pay.
                        {"\n\n"}This notice gives our opinion, not an official Medicare decision. If
                        you have other questions on this notice or on Medicare billing, call
                        1-800-MEDICARE (1-800-633-4227/TTY: 1-877-486-2048). Signing below means
                        that you have received and understand this notice. You also receive a copy.
                    </Text>
                </CardBody>
            </Card>

            {/* Hidden Tests + Signature (Page 2) */}
            <Card ref={testsRef} w="816px" style={{ position: "absolute", top: "-9999px" }}>
                <CardBody p={10}>
                    {customIcdTests.map((sel, idx) => (
                        <Box key={idx} mb={2} pl={2}>
                            <Text fontWeight="bold">{sel.test?.label || "Unnamed Test"}</Text>
                            <Text fontSize="sm" color="gray.600">
                                ICD-10: {sel.icdSelected?.label}
                            </Text>
                        </Box>
                    ))}
                    <Box mt={4} p={3} borderWidth="1px" rounded="md">
                        <Text fontSize="sm" color="gray.500">
                            Signature: ______________________________________________
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date:
                            _____________________
                        </Text>
                    </Box>
                </CardBody>
            </Card>

            {/* Button trigger */}
            <Box>
                <Center>
                    <Button
                        onClick={downloadAbnPdf}
                        colorScheme="green"
                        leftIcon={<FaFileDownload />}
                        width="auto"   // ✅ makes it normal width
                    >
                        Download ABN
                    </Button>
                </Center>
            </Box>

        </>
    );
};

export default AbnPdf;
